//
// Copyright 2025 DXOS.org
//

import * as Effect from 'effect/Effect';

import * as AppGraphNode from '@dxos/app-graph/AppGraphNode';
import * as EffectEx from '@dxos/effect/EffectEx';
import { type MenuActionProperties, type MenuItemGroupProperties } from '@dxos/ui-types';
import { resolveKeyBinding } from '@dxos/util';

import { type MenuAction, type MenuItemGroup, type MenuSeparator } from './types.ts';

/**
 * Execute a menu action's Effect with its captured context.
 * This provides the `_actionContext` layer if available.
 */
export const executeMenuAction = async (action: MenuAction, params: AppGraphNode.InvokeProps = {}): Promise<void> => {
  let effect = action.data(params);

  // Provide captured action context if available.
  if (action._actionContext) {
    effect = effect.pipe(Effect.provide(action._actionContext));
  }

  await EffectEx.runAndForwardErrors(effect);
};

export const getShortcut = (action: AppGraphNode.ActionLike) => {
  return resolveKeyBinding(action.properties?.keyBinding);
};

export const fallbackIcon = 'ph--circle-dashed--regular';

export const createMenuAction = <P extends {} = {}>(
  id: string,
  invoke: (params?: AppGraphNode.InvokeProps) => void,
  properties: P & MenuActionProperties,
) =>
  ({
    id,
    type: AppGraphNode.ActionType,
    properties,
    data: (params?: AppGraphNode.InvokeProps) => Effect.sync(() => invoke(params)),
  }) satisfies MenuAction;

export const createMenuItemGroup = <P extends MenuItemGroupProperties>(id: string, properties: P) =>
  ({
    id,
    type: AppGraphNode.ActionGroupType,
    properties,
    data: AppGraphNode.actionGroupSymbol,
  }) satisfies MenuItemGroup;

export const createGapSeparator = (id: string = 'gap', source: string = 'root') => ({
  nodes: [
    {
      id,
      type: '@dxos/react-ui-toolbar/separator',
      properties: { variant: 'gap' },
      data: undefined as never,
    } satisfies MenuSeparator,
  ],
  edges: [
    {
      source,
      target: id,
      relation: 'child' as const,
    },
  ],
});

export const createLineSeparator = (id: string = 'line', source: string = 'root') => ({
  nodes: [
    {
      id,
      type: '@dxos/react-ui-toolbar/separator',
      properties: { variant: 'line' },
      data: undefined as never,
    } satisfies MenuSeparator,
  ],
  edges: [{ source, target: id, relation: 'child' as const }],
});
